import type { Ref } from "vue";
import type { PerfEval, Scenario } from "./forms/formTypes";
import type { ScenarioCalculations } from "./scenarioCalculations/calculationTypes";

import { v4 } from "uuid";
import { format } from "date-fns";
import { convert } from "@/libs/units";
import { GasFlowRateUnits } from "@/libs/units/flowRate";
import { Override_Options_Enum, Sizing_Pumping_Type_Enum } from "gql/api";

export default (
  scenario: Ref<Scenario | null | undefined>,
  scenarioCalculations: ScenarioCalculations,
  parentId?: string | null | undefined
): (() => PerfEval) => {
  const isHPS = scenario.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
  return () => {
    const performanceEvaluationId = v4();
    const res: PerfEval = {
      __typename: "performance_evaluations",
      id: performanceEvaluationId,
      winning_revision_id: null,
      scenario_id: scenario.value?.id,
      parent_id: parentId,
      title: format(new Date(), "MMMM-dd-yyyy hh:mm"),
      pump_intake_pressure_psia: isHPS
        ? scenario.value?.well_properties?.hps_pump_inlet_pressure_psia
        : scenarioCalculations.pumpIntakePressurePSIA.value,
      pump_hz: scenario.value?.parameters?.pump_hz,
      pump_wear_head_correction_ratio:
        scenario.value?.parameters?.pump_wear_head_correction_ratio,
      pump_wear_brake_horsepower_correction_ratio:
        scenario.value?.parameters?.pump_wear_brake_horsepower_correction_ratio,
      gas_rate_mscf_day: convert(
        scenarioCalculations.surfaceFlowRates.value.gasFlowRateSCFD,
        GasFlowRateUnits.CubicFeetPerDay,
        GasFlowRateUnits.ThousandCubicFeetPerDay
      ),
      oil_rate_std_barrel_day:
        scenarioCalculations.surfaceFlowRates.value.oilFlowRateSTBD,
      water_rate_std_barrel_day:
        scenarioCalculations.surfaceFlowRates.value.waterFlowRateSTBD,
      casing_pressure_psia:
        scenario.value?.parameters?.bottom_casing_pressure_psia,
      tubing_pressure_psia:
        scenario.value?.parameters?.bottom_tubing_pressure_psia,
      intake_override_gas_separation_percent:
        scenario.value?.parameters?.intake_override_gas_separation_percent,
      natural_override_gas_separation_percent:
        scenario.value?.parameters?.natural_override_gas_separation_percent,
      intake_gas_separation_override_type:
        scenario.value?.parameters?.intake_gas_separation_override_type,
      natural_gas_separation_override_type: isHPS
        ? scenario.value?.parameters?.natural_gas_separation_override_type
        : Override_Options_Enum.Calculate,
      r_xref_pumps:
        scenario.value?.equipment?.r_xref_pumps.map(xrefPump => ({
          __typename: "performance_evaluations_xref_pumps",
          id: v4(),
          winning_revision_id: null,
          performance_evaluation_id: performanceEvaluationId,
          xref_pump_id: xrefPump.id,
          brake_horsepower_correction_ratio:
            xrefPump.brake_horsepower_correction_ratio,
          head_correction_ratio: xrefPump.head_correction_ratio,
          rate_correction_ratio: xrefPump.rate_correction_ratio
        })) || []
    };

    return res;
  };
};
